import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Users, MessageSquare, Send, Pencil, CreditCard, Shield, Clock, Eye, EyeOff, DoorOpen, Megaphone, UserCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminManagers() {
  const { api, isAdmin, loading: authLoading, token } = useAuth();
  const navigate = useNavigate();
  const [managers, setManagers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editManager, setEditManager] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company_name: "", plan_id: "", member_quota: 5, phone: "" });

  // Broadcast state
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastContent, setBroadcastContent] = useState("");
  const [broadcastType, setBroadcastType] = useState("all");
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [sentBroadcasts, setSentBroadcasts] = useState([]);

  // Guests state
  const [guests, setGuests] = useState([]);

  // Plan form
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: "", description: "", monthly_price: 0, included_hours: 0,
    is_unlimited: false, hide_prices: false, allowed_space_ids: [], max_members: 10, is_active: true
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate("/login"); return; }
    loadAll();
  }, [isAdmin, authLoading]);

  const loadAll = async () => {
    try {
      const [m, msgs, p, s, bc, g] = await Promise.all([
        api("get", "/admin/managers"),
        api("get", "/admin/messages"),
        api("get", "/admin/plans"),
        api("get", "/spaces"),
        api("get", "/admin/messages/sent").catch(() => ({ data: [] })),
        api("get", "/guests").catch(() => ({ data: [] })),
      ]);
      setManagers(m.data);
      setMessages(msgs.data);
      setPlans(p.data);
      setSpaces(s.data);
      setSentBroadcasts(bc.data || []);
      setGuests(g.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const openEditManager = (m) => {
    setEditForm({
      name: m.name || "",
      email: m.email || "",
      company_name: m.company_name || "",
      phone: m.phone || "",
      plan_id: m.plan_id || "",
      member_quota: m.member_quota || 5
    });
    setEditManager(m);
  };

  const saveManager = async () => {
    if (!editManager) return;
    try {
      const payload = { ...editForm };
      if (payload.plan_id === "none" || payload.plan_id === "") payload.plan_id = null;
      await api("put", `/admin/managers/${editManager.id}`, payload);
      toast.success("Manager updated");
      setEditManager(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const addManager = async () => {
    if (!form.name || !form.email || !form.company_name) {
      toast.error("Name, email, and company name are required"); return;
    }
    try {
      await api("post", "/admin/managers", form);
      toast.success("Manager created — credentials sent by email");
      setShowAdd(false);
      setForm({ name: "", email: "", company_name: "", plan_id: "", member_quota: 5, phone: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteManager = async (id) => {
    if (!window.confirm("Remove this manager? Their members will remain.")) return;
    try {
      await api("delete", `/admin/managers/${id}`);
      toast.success("Manager removed");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendReply = async (msgId) => {
    if (!replyText.trim()) return;
    try {
      await api("put", `/admin/messages/${msgId}/reply`, { reply: replyText });
      toast.success("Reply sent");
      setReplyTo(null);
      setReplyText("");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Plan CRUD
  const openPlanForm = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({ ...plan });
    } else {
      setEditingPlan(null);
      setPlanForm({ name: "", description: "", monthly_price: 0, included_hours: 0, is_unlimited: false, hide_prices: false, allowed_space_ids: [], max_members: 10, is_active: true });
    }
    setShowPlanForm(true);
  };

  const savePlan = async () => {
    if (!planForm.name) { toast.error("Plan name required"); return; }
    try {
      if (editingPlan) {
        await api("put", `/admin/plans/${editingPlan.id}`, planForm);
        toast.success("Plan updated");
      } else {
        await api("post", "/admin/plans", planForm);
        toast.success("Plan created");
      }
      setShowPlanForm(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await api("delete", `/admin/plans/${id}`);
      toast.success("Plan deleted");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleSpaceInPlan = (spaceId) => {
    setPlanForm(p => ({
      ...p,
      allowed_space_ids: p.allowed_space_ids.includes(spaceId)
        ? p.allowed_space_ids.filter(id => id !== spaceId)
        : [...p.allowed_space_ids, spaceId]
    }));
  };

  if (loading) return <div className="max-w-6xl mx-auto px-6 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

  const unreadCount = messages.filter(m => m.status === "unread").length;
  const getPlanName = (planId) => plans.find(p => p.id === planId)?.name || "No plan";

  const sendBroadcast = async () => {
    if (!broadcastSubject.trim() || !broadcastContent.trim()) {
      toast.error("Subject and message content are required"); return;
    }
    setBroadcastSending(true);
    try {
      const res = await api("post", "/admin/messages/broadcast", {
        subject: broadcastSubject.trim(),
        content: broadcastContent.trim(),
        recipient_type: broadcastType,
      });
      toast.success(res.data.message);
      setBroadcastSubject(""); setBroadcastContent(""); setBroadcastType("all");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setBroadcastSending(false); }
  };

  const checkOutGuest = async (guestId) => {
    try {
      await api("put", `/guests/${guestId}/check-out`);
      toast.success("Guest checked out");
      loadAll();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 animate-fade-in" data-testid="admin-managers">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl">Managers, Plans & Messages</h1>
        <p className="text-muted-foreground mt-1">Manage plans, business managers and communications</p>
      </div>

      <Tabs defaultValue="plans" className="space-y-6">
        <TabsList className="bg-muted rounded-none">
          <TabsTrigger value="plans" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-plans">
            <CreditCard className="h-4 w-4 mr-2" />Plans
          </TabsTrigger>
          <TabsTrigger value="managers" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-admin-managers">
            <Building2 className="h-4 w-4 mr-2" />Managers
          </TabsTrigger>
          <TabsTrigger value="messages" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-admin-messages">
            <MessageSquare className="h-4 w-4 mr-2" />Messages
            {unreadCount > 0 && <Badge className="ml-2 bg-orange-500 text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">{unreadCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="broadcast" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-broadcast">
            <Megaphone className="h-4 w-4 mr-2" />Broadcast
          </TabsTrigger>
          <TabsTrigger value="guests" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-guests">
            <UserCheck className="h-4 w-4 mr-2" />Guests
            {guests.filter(g => g.status === "checked_in").length > 0 && <Badge className="ml-2 bg-emerald-500 text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">{guests.filter(g => g.status === "checked_in").length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ═══════════ PLANS TAB ═══════════ */}
        <TabsContent value="plans" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-xl">Company Plans</h2>
            <Button onClick={() => openPlanForm()} className="bg-foreground text-background rounded-none text-sm" data-testid="add-plan-btn">
              <Plus className="h-4 w-4 mr-1" />Create Plan
            </Button>
          </div>

          {plans.length === 0 ? (
            <Card className="p-12 text-center border border-border">
              <CreditCard className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No plans created yet. Create one to assign to managers.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
              {plans.map(plan => (
                <Card key={plan.id} className={`p-5 border ${plan.is_active ? "border-border" : "border-dashed border-gray-300 opacity-60"}`} data-testid={`plan-card-${plan.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading font-bold text-lg">{plan.name}</h3>
                      <p className="text-xs text-muted-foreground">{plan.description}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openPlanForm(plan)} data-testid={`edit-plan-${plan.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deletePlan(plan.id)} data-testid={`delete-plan-${plan.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <Separator className="my-3" />
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monthly Price</span>
                      <span className="font-bold">XCG {plan.monthly_price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Hours</span>
                      <span className="font-medium">{plan.is_unlimited ? "Unlimited" : `${plan.included_hours}h/month`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Max Members</span>
                      <span className="font-medium">{plan.max_members}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Hide Prices</span>
                      {plan.hide_prices ? <Badge className="bg-green-100 text-green-700 text-xs"><EyeOff className="h-3 w-3 mr-1" />Hidden</Badge> : <Badge variant="outline" className="text-xs"><Eye className="h-3 w-3 mr-1" />Visible</Badge>}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Room Access</span>
                      <span className="text-xs font-medium">{plan.allowed_space_ids?.length > 0 ? `${plan.allowed_space_ids.length} rooms` : "All rooms"}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Companies</span>
                      <Badge variant="secondary" className="text-xs">{plan.manager_count || 0} managers</Badge>
                    </div>
                  </div>
                  {!plan.is_active && <Badge className="mt-3 bg-gray-200 text-gray-600 text-xs">Inactive</Badge>}
                </Card>
              ))}
            </div>
          )}

          {/* Plan Form Dialog */}
          <Dialog open={showPlanForm} onOpenChange={setShowPlanForm}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plan-form-dialog">
              <DialogHeader><DialogTitle>{editingPlan ? "Edit Plan" : "Create Plan"}</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <Label>Plan Name *</Label>
                  <Input value={planForm.name} onChange={e => setPlanForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Growth" data-testid="plan-name-input" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={planForm.description} onChange={e => setPlanForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description" data-testid="plan-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Monthly Price (XCG)</Label>
                    <Input type="number" value={planForm.monthly_price} onChange={e => setPlanForm(p => ({ ...p, monthly_price: parseFloat(e.target.value) || 0 }))} data-testid="plan-price-input" />
                  </div>
                  <div>
                    <Label>Max Members</Label>
                    <Input type="number" value={planForm.max_members} onChange={e => setPlanForm(p => ({ ...p, max_members: parseInt(e.target.value) || 0 }))} data-testid="plan-max-members-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Included Hours/Month</Label>
                    <Input type="number" value={planForm.included_hours} onChange={e => setPlanForm(p => ({ ...p, included_hours: parseFloat(e.target.value) || 0 }))} disabled={planForm.is_unlimited} data-testid="plan-hours-input" />
                  </div>
                  <div className="flex items-end gap-3 pb-1">
                    <div className="flex items-center gap-2">
                      <Switch checked={planForm.is_unlimited} onCheckedChange={v => setPlanForm(p => ({ ...p, is_unlimited: v, included_hours: v ? 0 : p.included_hours }))} data-testid="plan-unlimited-toggle" />
                      <Label className="text-sm">Unlimited Hours</Label>
                    </div>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Hide Prices from Members</Label>
                    <p className="text-xs text-muted-foreground">Members won't see room prices during booking</p>
                  </div>
                  <Switch checked={planForm.hide_prices} onCheckedChange={v => setPlanForm(p => ({ ...p, hide_prices: v }))} data-testid="plan-hide-prices-toggle" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Active</Label>
                    <p className="text-xs text-muted-foreground">Inactive plans can't be assigned</p>
                  </div>
                  <Switch checked={planForm.is_active} onCheckedChange={v => setPlanForm(p => ({ ...p, is_active: v }))} data-testid="plan-active-toggle" />
                </div>
                <Separator />
                <div>
                  <Label className="text-sm font-medium mb-2 block">Room Access</Label>
                  <p className="text-xs text-muted-foreground mb-3">Select which rooms this plan grants access to. Leave empty for all rooms.</p>
                  <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                    {spaces.map(s => (
                      <label key={s.id} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${planForm.allowed_space_ids?.includes(s.id) ? "bg-primary/5 border-primary/30" : "border-border hover:bg-muted/50"}`}>
                        <input
                          type="checkbox"
                          checked={planForm.allowed_space_ids?.includes(s.id) || false}
                          onChange={() => toggleSpaceInPlan(s.id)}
                          className="rounded"
                        />
                        <DoorOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <span className="text-sm font-medium">{s.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{s.capacity} seats</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {planForm.allowed_space_ids?.length === 0 && (
                    <p className="text-xs text-green-600 mt-2 font-medium">All rooms accessible</p>
                  )}
                </div>
                <Button onClick={savePlan} className="w-full bg-foreground text-background rounded-none" data-testid="save-plan-btn">
                  {editingPlan ? "Update Plan" : "Create Plan"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ═══════════ MANAGERS TAB ═══════════ */}
        <TabsContent value="managers" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-xl">Business Managers</h2>
            <Button onClick={() => setShowAdd(true)} className="bg-foreground text-background rounded-none text-sm" data-testid="add-manager-btn">
              <Plus className="h-4 w-4 mr-1" />Add Manager
            </Button>
          </div>

          <div className="rounded-lg border overflow-x-auto" data-testid="managers-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Quota</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {managers.map(m => (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{m.company_name}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.email}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize text-xs">
                        {m.plan_id ? getPlanName(m.plan_id) : "No plan"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-bold">{m.members_created}</TableCell>
                    <TableCell>{m.member_quota}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditManager(m)} data-testid={`edit-manager-${m.id}`}><Pencil className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteManager(m.id)} data-testid={`delete-manager-${m.id}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {managers.length === 0 && <div className="p-12 text-center text-muted-foreground">No managers yet</div>}
          </div>

          {/* Add Manager Dialog */}
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogContent data-testid="add-manager-dialog">
              <DialogHeader><DialogTitle>Add Business Manager</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="manager-name-input" /></div>
                  <div><Label>Company *</Label><Input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} data-testid="manager-company-input" /></div>
                </div>
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="manager-email-input" /></div>
                <p className="text-xs text-muted-foreground -mt-1">A secure password will be auto-generated and emailed to the user.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Plan</Label>
                    <Select value={form.plan_id} onValueChange={v => setForm(p => ({ ...p, plan_id: v }))}>
                      <SelectTrigger data-testid="manager-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Plan</SelectItem>
                        {plans.filter(p => p.is_active).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name} (XCG {p.monthly_price}/mo)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Member Quota</Label>
                    <Input type="number" value={form.member_quota} onChange={e => setForm(p => ({ ...p, member_quota: parseInt(e.target.value) || 0 }))} data-testid="manager-quota-input" />
                  </div>
                </div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} /></div>
                <Button onClick={addManager} className="w-full bg-foreground text-background rounded-none" data-testid="confirm-add-manager-btn">Create Manager</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Manager Dialog */}
          <Dialog open={!!editManager} onOpenChange={() => setEditManager(null)}>
            <DialogContent className="sm:max-w-lg" data-testid="edit-manager-dialog">
              <DialogHeader><DialogTitle>Edit Manager</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Name</Label>
                    <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-manager-name" />
                  </div>
                  <div>
                    <Label>Company</Label>
                    <Input value={editForm.company_name} onChange={e => setEditForm(p => ({ ...p, company_name: e.target.value }))} data-testid="edit-manager-company" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-manager-email" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} data-testid="edit-manager-phone" />
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Plan</Label>
                    <Select value={editForm.plan_id || "none"} onValueChange={v => setEditForm(p => ({ ...p, plan_id: v }))}>
                      <SelectTrigger data-testid="edit-manager-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Plan</SelectItem>
                        {plans.filter(p => p.is_active).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name} (XCG {p.monthly_price}/mo)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Member Quota</Label>
                    <Input type="number" value={editForm.member_quota} onChange={e => setEditForm(p => ({ ...p, member_quota: parseInt(e.target.value) || 0 }))} data-testid="edit-manager-quota" />
                  </div>
                </div>
                <Button onClick={saveManager} className="w-full bg-foreground text-background rounded-none" data-testid="save-edit-manager-btn">
                  Save Changes
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ═══════════ MESSAGES TAB ═══════════ */}
        <TabsContent value="messages" className="space-y-4">
          <h2 className="font-heading font-semibold text-xl">All Messages</h2>
          {messages.length === 0 ? (
            <Card className="p-8 text-center border border-border"><MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No messages yet.</p></Card>
          ) : (
            <div className="space-y-3">
              {messages.map(m => (
                <Card key={m.id} className={`p-4 border ${m.status === "unread" ? "border-orange-300 bg-orange-50/30" : "border-border"}`} data-testid={`admin-msg-${m.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs capitalize">{m.message_type}</Badge>
                      <span className="text-sm font-medium">{m.sender_name}</span>
                      {m.company_name && <Badge variant="secondary" className="text-xs">{m.company_name}</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${m.status === "unread" ? "bg-orange-500 text-white" : "bg-green-100 text-green-700"}`}>{m.status}</Badge>
                      <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <p className="text-sm mb-2">{m.content}</p>
                  <p className="text-xs text-muted-foreground mb-2">From: {m.sender_email} ({m.sender_role})</p>

                  {m.reply ? (
                    <div className="bg-muted/50 rounded p-3 border-l-2 border-green-500">
                      <p className="text-xs font-medium text-green-700 mb-1">Your Reply:</p>
                      <p className="text-sm">{m.reply}</p>
                    </div>
                  ) : (
                    <div>
                      {replyTo === m.id ? (
                        <div className="space-y-2 pt-2">
                          <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." rows={2} data-testid={`reply-input-${m.id}`} />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => sendReply(m.id)} className="bg-foreground text-background rounded-none text-xs" data-testid={`send-reply-${m.id}`}><Send className="h-3 w-3 mr-1" />Send Reply</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setReplyTo(null); setReplyText(""); }}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setReplyTo(m.id)} data-testid={`reply-btn-${m.id}`}>Reply</Button>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ═══════════ BROADCAST TAB ═══════════ */}
        <TabsContent value="broadcast" className="space-y-4">
          <h2 className="font-heading font-semibold text-xl">Send Broadcast Message</h2>
          <p className="text-sm text-muted-foreground">Send a message to managers and/or members. They will receive it in-app and via email.</p>

          <Card className="p-6 border border-border space-y-4" data-testid="broadcast-form">
            <div>
              <Label>Recipients</Label>
              <Select value={broadcastType} onValueChange={setBroadcastType}>
                <SelectTrigger data-testid="broadcast-recipient-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers & Members</SelectItem>
                  <SelectItem value="managers">Managers Only</SelectItem>
                  <SelectItem value="members">Members Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={broadcastSubject} onChange={e => setBroadcastSubject(e.target.value)} placeholder="Message subject..." data-testid="broadcast-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={broadcastContent} onChange={e => setBroadcastContent(e.target.value)} placeholder="Write your message..." rows={5} data-testid="broadcast-content" />
            </div>
            <Button
              onClick={sendBroadcast} disabled={broadcastSending}
              className="bg-foreground text-background rounded-none"
              data-testid="broadcast-send-btn"
            >
              <Send className="h-4 w-4 mr-2" />{broadcastSending ? "Sending..." : "Send Broadcast"}
            </Button>
          </Card>

          {sentBroadcasts.length > 0 && (
            <>
              <Separator />
              <h3 className="font-heading font-semibold text-lg">Sent Broadcasts</h3>
              <div className="space-y-3">
                {sentBroadcasts.map(b => (
                  <Card key={b.broadcast_id} className="p-4 border border-border" data-testid={`broadcast-${b.broadcast_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{b.subject}</span>
                      <span className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{b.content}</p>
                    <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                      <span>Sent to {b.recipient_count} recipients</span>
                      <span>{b.read_count} read</span>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* ═══════════ GUESTS TAB ═══════════ */}
        <TabsContent value="guests" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-xl">Guest Registry</h2>
            <Badge variant="outline">{guests.filter(g => g.status === "checked_in").length} currently checked in</Badge>
          </div>
          {guests.length === 0 ? (
            <Card className="p-8 text-center border border-border"><UserCheck className="h-8 w-8 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No guests registered yet.</p></Card>
          ) : (
            <div className="rounded-lg border overflow-x-auto" data-testid="guests-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Visiting</TableHead>
                  <TableHead>Purpose</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
                  <TableHead>Time</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {guests.map(g => (
                    <TableRow key={g.id}>
                      <TableCell className="font-medium">{g.name}</TableCell>
                      <TableCell className="text-xs">{g.email || "—"}</TableCell>
                      <TableCell>{g.host_name || "—"}</TableCell>
                      <TableCell className="text-xs">{g.purpose || "—"}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs capitalize">{g.type?.replace(/_/g, " ")}</Badge></TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${g.status === "checked_in" ? "bg-emerald-100 text-emerald-700" : g.status === "expected" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}`}>
                          {g.status?.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{g.checked_in_at ? new Date(g.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                      <TableCell>
                        {g.status === "checked_in" && (
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => checkOutGuest(g.id)} data-testid={`checkout-guest-${g.id}`}>
                            Check Out
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
